import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CampoCompartido from '../../components/Field';
import SegundoFactor from '../../components/SegundoFactor';
import DescargarRespaldo from '../../components/DescargarRespaldo';
import { olvidarNombreApp } from '../../hooks/useNombreApp';
import { Settings, Save, Globe, Bell, CheckCircle, ShieldCheck, KeyRound, HardDriveDownload, Eye, EyeOff, AlertTriangle, RefreshCw } from 'lucide-react';

const API_CONFIG = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/public/config/`;

export default function GlobalSettings() {
    const [config, setConfig] = useState({
        app_name: '',
        subscription_alerts: true,
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    // Código de recuperación
    const [recoveryCode, setRecoveryCode] = useState(null);
    const [recoveryLoading, setRecoveryLoading] = useState(false);
    const [showCode, setShowCode] = useState(false);


    useEffect(() => {
        axios.get(API_CONFIG)
            .then(res => { if (res.data) setConfig({ app_name: res.data.app_name || '', subscription_alerts: res.data.subscription_alerts ?? true }); })
            .catch(e => console.error(e));
    }, []);

    const generateRecoveryCode = async () => {
        if (!window.confirm('¿Generar un nuevo código de recuperación? El código anterior quedará invalidado.')) return;
        setRecoveryLoading(true);
        setRecoveryCode(null);
        try {
            const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
            const res = await axios.post(`${BASE}/api/v1/auth/admin/generate-recovery-code/`, {});
            setRecoveryCode(res.data.code);
            setShowCode(true);
        } catch {
            alert('Error al generar el código.');
        } finally {
            setRecoveryLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage(null);
        try {
            await axios.post(`${API_CONFIG}update_config/`, config);
            // Sin esto, la barra lateral y los ingresos seguirian mostrando el
            // nombre anterior hasta recargar la pagina.
            olvidarNombreApp();
            setMessage('ok');
            setTimeout(() => setMessage(null), 3000);
        } catch {
            setMessage('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center gap-4 bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100">
                <div className="bg-brand-50 p-4 rounded-2xl text-brand-800"><Settings size={24} /></div>
                <div>
                    <h2 className="titulo-modal">Configuración Global</h2>
                    <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Preferencias del sistema SaaS</p>
                </div>
            </div>

            <form onSubmit={handleSave} className="space-y-4">
                {/* Nombre visible de la plataforma */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 space-y-6">
                    <h3 className="titulo-seccion">
                        <Globe size={14} /> Nombre de la Plataforma
                    </h3>

                    <Field label="Nombre de la App"
                        hint="Se muestra en el login y en el sidebar de todos los inquilinos.">
                        <input
                            type="text"
                            required
                            className={INPUT}
                            value={config.app_name}
                            onChange={e => setConfig({ ...config, app_name: e.target.value })}
                            placeholder="Ej: SoftecPY-POS"
                        />
                    </Field>
                </section>

                {/* Notificaciones */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8">
                    <h3 className="titulo-seccion mb-6">
                        <Bell size={14} /> Notificaciones
                    </h3>

                    <div className="flex items-start justify-between gap-6">
                        <div>
                            <p className="text-sm font-bold text-slate-800">Alertas de nuevo registro</p>
                            <p className="text-xs text-slate-500 mt-0.5 max-w-sm">
                                Recibir notificación cuando un cliente se auto-registra desde la landing page. Útil para saber si llegó un nuevo interesado aunque no estés en línea.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={() => setConfig({ ...config, subscription_alerts: !config.subscription_alerts })}
                            className={`shrink-0 w-12 h-6 rounded-full transition-colors relative ${config.subscription_alerts ? 'bg-brand-600' : 'bg-slate-200'}`}
                        >
                            <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${config.subscription_alerts ? 'left-7' : 'left-1'}`} />
                        </button>
                    </div>
                </section>

                {/* Seguridad */}
                <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 space-y-5">
                    <h3 className="titulo-seccion">
                        <ShieldCheck size={14} /> Seguridad y Recuperación de Acceso
                    </h3>

                    <p className="text-xs text-slate-500 leading-relaxed">
                        Si perdés tu contraseña de administrador podés recuperar el acceso con un código de emergencia.
                        Generá el código, guardalo en un lugar seguro (papel, gestor de contraseñas) y úsalo desde la pantalla
                        de <span className="font-bold text-slate-700">Recuperar Acceso</span> en el login.
                    </p>

                    <div className="bg-alerta-50 border border-alerta-200 rounded-2xl p-4 flex gap-3">
                        <AlertTriangle size={16} className="text-alerta-500 shrink-0 mt-0.5" />
                        <p className="text-xs text-alerta-700 font-medium">
                            El código se muestra <strong>una sola vez</strong> al generarlo. Si lo perdés, generá uno nuevo.
                            Cada código solo puede usarse <strong>una vez</strong> — al usarlo queda invalidado.
                        </p>
                    </div>

                    {recoveryCode ? (
                        <div className="bg-slate-900 rounded-2xl p-5 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Tu código de recuperación</span>
                                <button
                                    type="button"
                                    onClick={() => setShowCode(v => !v)}
                                    className="text-slate-400 hover:text-white transition-colors"
                                >
                                    {showCode ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                            </div>
                            {showCode ? (
                                <p className="font-mono text-xl font-black text-white tracking-[0.3em] text-center py-2">
                                    {recoveryCode}
                                </p>
                            ) : (
                                <p className="font-mono text-xl font-black text-slate-600 tracking-[0.3em] text-center py-2 select-none">
                                    ••••••-••••••-••••••-••••••
                                </p>
                            )}
                            <p className="text-[10px] text-exito-400 font-bold text-center">
                                ✓ Guardalo ahora — no se volverá a mostrar
                            </p>
                        </div>
                    ) : null}

                    <button
                        type="button"
                        onClick={generateRecoveryCode}
                        disabled={recoveryLoading}
                        className="flex items-center gap-2 px-5 py-3 rounded-2xl border-2 border-alerta-300 text-alerta-700 font-black uppercase tracking-widest text-xs hover:bg-alerta-50 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={recoveryLoading ? 'animate-spin' : ''} />
                        {recoveryCode ? 'Generar Nuevo Código' : 'Generar Código de Recuperación'}
                    </button>
                </section>

                {/* Guardar */}
                <div className="flex items-center gap-4">
                    <button
                        disabled={loading}
                        className="bg-slate-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs flex items-center gap-2 hover:bg-slate-800 transition-colors disabled:opacity-50 shadow-lg"
                    >
                        <Save size={16} />
                        {loading ? 'Guardando...' : 'Guardar Cambios'}
                    </button>

                    {message === 'ok' && (
                        <span className="flex items-center gap-1.5 text-exito-600 text-sm font-bold">
                            <CheckCircle size={16} /> Guardado correctamente
                        </span>
                    )}
                    {message === 'error' && (
                        <span className="text-error-500 text-sm font-bold">Error al guardar.</span>
                    )}
                </div>
            </form>

            {/* Fuera del formulario de arriba a propósito: este bloque tiene sus
                propios controles, y HTML no admite formularios anidados. */}
            <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 space-y-5 mt-4">
                <h3 className="titulo-seccion">
                    <KeyRound size={14} /> Verificación en dos pasos
                </h3>
                <SegundoFactor />
            </section>

            <section className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 space-y-5 mt-4">
                <h3 className="titulo-seccion">
                    <HardDriveDownload size={14} /> Respaldo descargable
                </h3>
                <DescargarRespaldo />
            </section>
        </div>
    );
}

// Mantiene el espaciado y la etiqueta sin margen inferior propios de esta
// pantalla; la asociación con el control la resuelve el componente compartido.
function Field(props) {
    return (
        <CampoCompartido
            {...props}
            className="space-y-1.5"
            labelClassName="text-[10px] font-black text-slate-400 uppercase tracking-widest px-1 block"
        />
    );
}

const INPUT = 'campo';
